import { FormEvent, useState } from "react";
import { Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { freebieTitle, useFreebie } from "@/hooks/useFreebie";
import { useSeason } from "@/hooks/useSeason";

export function SignupBox({ source, compact = false }: { source: string; compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const freebie = useFreebie();
  const season = useSeason();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/public/subscribe", { email, source });
      setMsg(data.message);
      setEmail("");
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`signup-box relative grid items-center gap-8 overflow-hidden rounded-3xl ${compact ? "p-6" : "p-8 sm:p-12 md:grid-cols-[1fr_auto]"}`} data-testid={`signup-box-${source}`}>
      <div className="relative z-10">
      <p className="shop-eyebrow mb-3">Free {season && season.key !== "everyday" ? `${season.cta} ` : ""}printable</p>
      <h2 className="shop-h2 max-w-lg text-2xl leading-tight sm:text-3xl" data-testid="signup-headline">Get {freebieTitle(freebie)}, plus new ideas by email.</h2>
      {msg ? (
        <p className="mt-5 rounded-xl bg-[#F5F5F4] px-4 py-3 font-semibold text-[#23324A]" data-testid="signup-success">{msg}</p>
      ) : (
        <form onSubmit={submit} className="mt-6 flex max-w-lg flex-col gap-3 sm:flex-row">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" aria-label="Email address"
            className="field flex-1 bg-white" data-testid="signup-email-input" />
          <button type="submit" disabled={busy} className="btn-solid justify-center disabled:opacity-60" data-testid="signup-submit-btn">
            <Mail className="h-4 w-4" />{busy ? "Sending…" : "Send my printable"}
          </button>
        </form>
      )}
      {err && <p className="mt-3 rounded-xl bg-[#FDE6DF] px-3 py-2 text-sm font-semibold text-[#23324A]" data-testid="signup-error">{err}</p>}
      <p className="mt-3 text-xs text-[#23324A]/65">One email with your PDF. No spam, ever.</p>
      </div>
      {!compact && freebie && (
        <img src={`${process.env.REACT_APP_BACKEND_URL}${freebie.preview}`} alt={`Preview of ${freebie.title}`} loading="lazy"
          className="mx-auto w-40 rotate-2 rounded-md border border-[#E7E5E4] bg-white shadow-md transition-transform duration-200 hover:rotate-0 sm:w-48" data-testid="signup-freebie-preview" />
      )}
    </section>
  );
}
